import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Crown, UserPlus, Gamepad2, BarChart3, Filter, Pencil, ListOrdered, History, CalendarClock, Coins, Trash2 } from 'lucide-react'
import { RootState } from '../store'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { FOOTBALL_LIVE_POLL_MS } from '../utils/footballLiveScore'
import { setRoomMatches, setLoading } from '../store/matchesSlice'
import { setSingleRoom } from '../store/roomsSlice'
import matchService, { type BestPredictionFromApi } from '../services/matchService'
import roomsService from '../services/roomsService'
import { User } from '../types/room'
import InvitePlayerModal from '../components/InvitePlayerModal'
import SetStartingPointsModal from '../components/SetStartingPointsModal'
import ViewPlayerStatsGamesModal from '../components/ViewPlayerStatsGamesModal'
import BestOfModal from '../components/BestOfModal'
import EditRoomModal from '../components/EditRoomModal'
import LeaveRoomModal from '../components/LeaveRoomModal'
import DeleteRoomModal from '../components/DeleteRoomModal'
import PlayWorldFromRoomModal from '../components/PlayWorldFromRoomModal'
import PlayWorldRoomBanner from '../components/PlayWorldRoomBanner'
import LeaveWorldFootballModal from '../components/LeaveWorldFootballModal'
import RoomRulesModal from '../components/RoomRulesModal'
import GamesAndPredictionsModal from '../components/GamesAndPredictionsModal'
import GamePredictionsModal from '../components/GamePredictionsModal'
import type { PlayedMatch } from '../services/matchService'
import axios, { CancelTokenSource } from 'axios'
import { canManageRoom } from '../utils/roomPermissions'
import { useRoomMessenger } from '../hooks/useRoomMessenger'
import RoomChatRulesActions from '../components/room/RoomChatRulesActions'
import RoomMessengerModal from '../components/room/RoomMessengerModal'

const rankBadge = (rank: number) => {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}

const Room: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { isLoading } = useSelector((state: RootState) => state.matches)
  const { singleRoom } = useSelector((state: RootState) => state.rooms)
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  
  const room = singleRoom
  const isOwner = canManageRoom(room, currentUser?._id)
  const isFootballRoom = (room?.gameType ?? 'football') === 'football'

  const { data: publicFootballStatus, refetch: refetchPublicFootballStatus } = useQuery({
    queryKey: ['public-football-status', roomId],
    queryFn: () => roomsService.getPublicFootballStatus(roomId!),
    enabled: Boolean(roomId) && isFootballRoom,
    staleTime: 30 * 1000,
  })

  const showPlayWorldCta = Boolean(
    isFootballRoom &&
    publicFootballStatus?.syncEnabled &&
    publicFootballStatus.roomLeagues.length > 0 &&
    !publicFootballStatus.fullyEnrolled,
  )

  const showViewWorldLink = Boolean(
    isFootballRoom &&
    publicFootballStatus?.syncEnabled &&
    publicFootballStatus.fullyEnrolled,
  )
  const {
    showMessengerModal,
    setShowMessengerModal,
    unreadMessageCount,
    setUnreadMessageCount,
  } = useRoomMessenger(roomId)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [startingPointsPlayer, setStartingPointsPlayer] = useState<User | null>(null)
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showLeaveRoomModal, setShowLeaveRoomModal] = useState(false)
  const [showDeleteRoomModal, setShowDeleteRoomModal] = useState(false)
  const [showPlayWorldModal, setShowPlayWorldModal] = useState(false)
  const [showLeaveWorldModal, setShowLeaveWorldModal] = useState(false)
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState<User | null>(null)
  const [modalViewType, setModalViewType] = useState<'stats' | 'games' | 'upcoming'>('stats')
  const [showBestOfModal, setShowBestOfModal] = useState(false)
  const [showGamesAndPredictionsModal, setShowGamesAndPredictionsModal] = useState(false)
  const [showPlayedModal, setShowPlayedModal] = useState(false)
  const [showGamePredictionsModal, setShowGamePredictionsModal] = useState(false)
  const [selectedGameForPredictions, setSelectedGameForPredictions] = useState<PlayedMatch | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false) // Shows subtle indicator while refreshing in background
  const [isLoadingPlayerData, setIsLoadingPlayerData] = useState(false) // Loading first page of player predictions
  const [isLoadingMorePredictions, setIsLoadingMorePredictions] = useState(false) // Loading next page (Load more)
  const [hasMorePlayerPredictions, setHasMorePlayerPredictions] = useState(false)
  const [isLoadingBestOf, setIsLoadingBestOf] = useState(false) // Loading best predictions for BestOf modal
  const [bestPredictionsFromApi, setBestPredictionsFromApi] = useState<BestPredictionFromApi[] | null>(null)
  const hasFetchedRef = React.useRef(false) // Prevent duplicate fetches
  const currentRoomIdRef = React.useRef<string | null>(null) // Track room ID changes
  const playerDataRequestRef = useRef<CancelTokenSource | null>(null) // Cancel token for player data requests

  // Ensure when entering a room the viewport shows the top of the dashboard (header, actions)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [roomId])

  // Immediately show cached data on mount (before any async operations)
  useEffect(() => {
    if (roomId && roomId !== currentRoomIdRef.current) {
      currentRoomIdRef.current = roomId
      hasFetchedRef.current = false // Reset fetch flag for new room
      
      const cachedRoom = roomsService.getCachedRoom(roomId)
      if (cachedRoom) {
        console.log('⚡ Showing cached room immediately:', cachedRoom.name)
        dispatch(setSingleRoom(cachedRoom))
      }
    }
  }, [roomId, dispatch])

  // Compute user points - use pre-aggregated totalPoints if available (from summary endpoint)
  // Falls back to calculating from predictions for full room data
  const computeUserPoints = useCallback((player: User): number => {
    // Use pre-aggregated totalPoints from summary endpoint (fast path)
    if (typeof (player as any).totalPoints === 'number') {
      return (player as any).totalPoints
    }
    
    // Fallback: calculate from predictions (for full room data or legacy)
    if (!player.predictions || !Array.isArray(player.predictions)) {
      return 0 // No warning needed - summary endpoint has empty predictions array
    }
    
    const points = player.predictions.reduce((acc: number, prediction) => {
      if (!prediction || typeof prediction !== 'object') {
        return acc
      }
      const scorePoints = Number(prediction.predictedScorePoint) || 0
      const winnerPoints = Number(prediction.predictedWinnerPoint) || 0
      return acc + scorePoints + winnerPoints
    }, 0)
    return points
  }, [])

  // Sort players by points (same as React Native app)
  const sortedPlayers = useMemo(() => {
    if (!room) {
      return []
    }
    return [...room.players].sort((a, b) => computeUserPoints(b) - computeUserPoints(a))
  }, [room, computeUserPoints])


  // React Query: cache room summary so revisiting the same room is instant (staleTime 1 min)
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    refetch: refetchSummary,
    error: summaryError,
  } = useQuery({
    queryKey: ['room-summary', roomId],
    queryFn: () => roomsService.getRoomSummary(roomId!),
    enabled: Boolean(roomId),
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  // Sync React Query result to Redux for the rest of the app
  useEffect(() => {
    if (summaryData) {
      dispatch(setSingleRoom(summaryData))
    }
  }, [summaryData, dispatch])

  useEffect(() => {
    const status = (summaryError as any)?.response?.status
    if (summaryError && (status === 404 || status === 403)) {
      dispatch(setSingleRoom(null))
    }
  }, [summaryError, dispatch])

  // React Query: cache room matches (staleTime 1 min) so revisiting is instant
  const { data: matchesData } = useQuery({
    queryKey: ['room-matches', roomId],
    queryFn: () => matchService.getRoomMatches(roomId!),
    enabled: Boolean(roomId) && Boolean(summaryData),
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  useEffect(() => {
    if (roomId && matchesData !== undefined) {
      dispatch(setRoomMatches({ roomId, matches: matchesData }))
    }
  }, [roomId, matchesData, dispatch])

  // Loading state: show spinner only when no data yet
  useEffect(() => {
    dispatch(setLoading(summaryLoading && !summaryData))
  }, [summaryLoading, summaryData, dispatch])

  useEffect(() => {
    setIsRefreshing(summaryFetching && !!summaryData)
  }, [summaryFetching, summaryData])

  const fetchRoomData = useCallback(
    async (forceRefresh = false) => {
      if (!roomId) return
      if (forceRefresh) {
        await refetchSummary()
        await queryClient.invalidateQueries({ queryKey: ['room-matches', roomId] })
      }
    },
    [roomId, refetchSummary, queryClient],
  )

  const resolvePlayerId = (p: User) => p._id ?? (p as { id?: string }).id ?? ''

  // Always fetch the full list; Games/Upcoming/Stats filter client-side in the modal.
  const fetchPlayerPredictions = useCallback(async (
    player: User,
    cancelTokenSource: CancelTokenSource,
    opts?: { updateRoom?: boolean },
  ): Promise<boolean> => {
    const playerId = resolvePlayerId(player)
    if (!roomId || !playerId) return false
    try {
      let result = await matchService.getRoomPlayerPredictions(roomId, playerId, {
        cancelToken: cancelTokenSource.token,
        limit: 100,
        offset: 0,
      })
      if (cancelTokenSource.token.reason) return false

      let predictions: User['predictions'] = (result.data ?? []) as User['predictions']

      if (!predictions.length) {
        const fullRoom = await roomsService.getRoom(roomId, { skipCache: true })
        if (cancelTokenSource.token.reason) return false
        const fromRoom = fullRoom.players?.find(p => resolvePlayerId(p) === playerId)
        if (fromRoom?.predictions?.length) {
          predictions = fromRoom.predictions
          result = { ...result, data: result.data, hasMore: false }
        }
      }

      const updatedPlayer = { ...player, _id: playerId, predictions }
      setSelectedPlayer(updatedPlayer)
      setHasMorePlayerPredictions(result.hasMore)

      if (room?.players && opts?.updateRoom !== false) {
        const updatedPlayers = room.players.map(p =>
          resolvePlayerId(p) === playerId ? updatedPlayer : p
        )
        dispatch(setSingleRoom({ ...room, players: updatedPlayers }))
      }
      return predictions.length > 0
    } catch (error: any) {
      if (error?.message === 'New request initiated') return false
      console.error('Failed to load player predictions:', error)
      return false
    }
  }, [roomId, room, dispatch])

  const handlePlayerStats = (player: User) => {
    if (playerDataRequestRef.current) {
      playerDataRequestRef.current.cancel('New request initiated')
      playerDataRequestRef.current = null
    }

    setSelectedPlayer(player)
    setModalViewType('stats')
    setShowPlayerModal(true)
    setIsLoadingPlayerData(false)
    setHasMorePlayerPredictions(false)
  }

  const loadPlayerModalPredictions = async (player: User, cancelTokenSource: CancelTokenSource) => {
    let ok = await fetchPlayerPredictions(player, cancelTokenSource, { updateRoom: false })
    if (!ok && !cancelTokenSource.token.reason && roomId) {
      await new Promise(r => setTimeout(r, 600))
      if (cancelTokenSource.token.reason) return
      ok = await fetchPlayerPredictions(player, cancelTokenSource, { updateRoom: false })
    }
    if (!ok && !cancelTokenSource.token.reason && roomId) {
      const fullRoom = await roomsService.getRoom(roomId, { skipCache: true })
      if (!cancelTokenSource.token.reason) {
        const playerId = resolvePlayerId(player)
        const updatedPlayer = fullRoom.players.find(p => resolvePlayerId(p) === playerId)
        if (updatedPlayer?.predictions?.length) {
          setSelectedPlayer(updatedPlayer)
          setHasMorePlayerPredictions(false)
        }
      }
    }
  }

  const handlePlayerGames = async (player: User) => {
    if (playerDataRequestRef.current) {
      playerDataRequestRef.current.cancel('New request initiated')
    }

    setSelectedPlayer(player)
    setModalViewType('games')
    setShowPlayerModal(true)

    setIsLoadingPlayerData(true)
    setHasMorePlayerPredictions(false)
    const cancelTokenSource = axios.CancelToken.source()
    playerDataRequestRef.current = cancelTokenSource

    try {
      await loadPlayerModalPredictions(player, cancelTokenSource)
    } catch (e: any) {
      if (e?.message !== 'New request initiated') {
        console.error('Failed to load player played predictions:', e)
      }
    } finally {
      if (!cancelTokenSource.token.reason) setIsLoadingPlayerData(false)
      playerDataRequestRef.current = null
    }
  }

  const handlePlayerUpcoming = async (player: User) => {
    if (playerDataRequestRef.current) {
      playerDataRequestRef.current.cancel('New request initiated')
    }

    setSelectedPlayer(player)
    setModalViewType('upcoming')
    setShowPlayerModal(true)

    setIsLoadingPlayerData(true)
    setHasMorePlayerPredictions(false)
    const cancelTokenSource = axios.CancelToken.source()
    playerDataRequestRef.current = cancelTokenSource

    try {
      await loadPlayerModalPredictions(player, cancelTokenSource)
    } catch (e: any) {
      if (e?.message !== 'New request initiated') {
        console.error('Failed to load player upcoming predictions:', e)
      }
    } finally {
      if (!cancelTokenSource.token.reason) setIsLoadingPlayerData(false)
      playerDataRequestRef.current = null
    }
  }

  const handleLoadMorePredictions = async () => {
    if (!roomId || !selectedPlayer || isLoadingMorePredictions || !hasMorePlayerPredictions) return
    const playerId = resolvePlayerId(selectedPlayer)
    if (!playerId) return
    setIsLoadingMorePredictions(true)
    try {
      const offset = selectedPlayer.predictions?.length ?? 0
      const result = await matchService.getRoomPlayerPredictions(roomId, playerId, {
        limit: 50,
        offset,
      })
      const mergedData = result.data ?? []
      const existing = selectedPlayer.predictions ?? []
      const merged = [...existing, ...mergedData]
      const updatedPlayer = { ...selectedPlayer, predictions: merged }
      setSelectedPlayer(updatedPlayer)
      setHasMorePlayerPredictions(result.hasMore)
    } catch (error) {
      console.error('Failed to load more predictions:', error)
    } finally {
      setIsLoadingMorePredictions(false)
    }
  }

  const handleOpenBestOf = async () => {
    if (!roomId) return

    setShowBestOfModal(true)
    setBestPredictionsFromApi(null)
    setIsLoadingBestOf(true)

    try {
      const list = await matchService.getBestPredictions(roomId)
      setBestPredictionsFromApi(list)
    } catch (error: any) {
      console.error('Failed to load best predictions:', error)
      setBestPredictionsFromApi([])
    } finally {
      setIsLoadingBestOf(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent"></div>
      </div>
    )
  }

  if (!room && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center px-4">
          <div className="text-6xl mb-4">🏠</div>
          <h2 className="text-display-2 font-bold text-foreground mb-4">{t('roomPage.notFound')}</h2>
          <p className="text-muted-foreground mb-6">{t('roomPage.notFoundDesc')}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            {t('nav.backToHome')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-20 pb-[max(3rem,env(safe-area-inset-bottom))]">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          {/* Room Header - breakroom design */}
          <div className="mb-5 sm:mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-3 sm:mb-4">
              <Link to="/" className="inline-flex items-center gap-1.5 text-small sm:text-nav text-muted-foreground hover:text-foreground transition-colors group">
                <ArrowLeft size={14} className="sm:w-4 sm:h-4 group-hover:-translate-x-0.5 transition-transform" /> {t('nav.backToHome')}
              </Link>
              {!isOwner && roomId && (
                <button
                  type="button"
                  onClick={() => setShowLeaveRoomModal(true)}
                  className="text-xs text-destructive/90 hover:text-destructive"
                >
                  {t('room.leave.action')}
                </button>
              )}
              {isOwner && roomId && (
                <button
                  type="button"
                  onClick={() => setShowDeleteRoomModal(true)}
                  className="inline-flex items-center gap-1 text-xs text-destructive/90 hover:text-destructive"
                >
                  <Trash2 size={12} />
                  {t('room.delete.action')}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <h1 className="font-display text-xl sm:text-display-1 font-bold tracking-tight text-foreground truncate flex items-center gap-2">
                {room?.name}
                {isRefreshing && (
                  <span className="inline-block w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" title={t('roomPage.updating')} />
                )}
              </h1>
            </div>
            <p className="text-small sm:text-body-lg text-muted-foreground mt-1">
              {t('roomPage.seasonActive', { count: String(sortedPlayers.length) })}
            </p>
            {(showPlayWorldCta || showViewWorldLink) && (
              <PlayWorldRoomBanner
                className="mt-3"
                showPlayWorldCta={showPlayWorldCta}
                showViewWorldLink={showViewWorldLink}
                onOpenModal={() => setShowPlayWorldModal(true)}
                onLeaveWorld={() => setShowLeaveWorldModal(true)}
              />
            )}
          </div>

          {/* Action Buttons — mobile: Play + row of 3; desktop: single row of 4 (football) */}
          <div className={`grid gap-1.5 sm:gap-2 lg:gap-3 mb-6 sm:mb-10 ${room?.gameType === 'football' ? 'grid-cols-1 lg:grid-cols-4' : 'grid-cols-3'}`}>
            <button
              onClick={() => {
                const gameType = room?.gameType || 'football'
                if (gameType === 'football') navigate(`/predictions/${roomId}`)
                else if (gameType === 'chess') navigate(`/chess/${roomId}`)
                else if (gameType === 'golf') navigate(`/golf/${roomId}`)
                else if (gameType === 'formula1') navigate(`/formula1/${roomId}`)
              }}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-green-900 to-green-500 text-white font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-1.5 sm:gap-2 min-w-0"
            >
              <Play size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.play')}
            </button>
            {room?.gameType === 'football' && (
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 lg:contents">
                <button
                  onClick={handleOpenBestOf}
                  className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-warning to-destructive text-foreground font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-1.5 sm:gap-2 min-w-0"
                >
                  <Crown size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.bestOf')}
                </button>
                <button
                  onClick={() => setShowPlayedModal(true)}
                  className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-sky-800 to-sky-400 text-white font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-1.5 sm:gap-2 min-w-0"
                >
                  <History size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.played')}
                </button>
                <button
                  onClick={() => setShowGamesAndPredictionsModal(true)}
                  className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-red-900 to-red-500 text-white font-semibold text-[11px] xs:text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-1.5 sm:gap-2 min-w-0 px-1"
                >
                  <ListOrdered size={14} className="sm:w-4 sm:h-4 shrink-0" />
                  <span className="truncate">{t('roomPage.gamesAndPredictions')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Ranking list */}
          <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-3 sm:mb-4 min-w-0">
              <h2 className="font-display text-base sm:text-lg font-semibold flex items-center gap-1.5 text-foreground shrink-0 col-start-1">
                🏆 {t('roomPage.ranking')}
              </h2>
              {room?.gameType === 'football' && (
                <RoomChatRulesActions
                  className="col-start-2"
                  unreadCount={unreadMessageCount}
                  onChatClick={() => setShowMessengerModal(true)}
                  onRulesClick={() => setShowRulesModal(true)}
                />
              )}
              <div className="flex items-center justify-end gap-0.5 sm:gap-1 col-start-3 min-w-0">
              <button
                onClick={() => fetchRoomData(true)}
                disabled={isRefreshing}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.refresh')}
              >
                <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              {isOwner && room?.gameType === 'football' && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                  title={t('room.editRoom')}
                >
                  <Pencil size={16} className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowInviteModal(true)}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.invitePlayer')}
              >
                <UserPlus size={16} className="w-4 h-4" />
              </button>
              <button className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0" aria-label="Filter">
                <Filter size={14} className="w-4 h-4" />
              </button>
              </div>
            </div>

            {sortedPlayers.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-6xl mb-4">🏆</div>
                <h3 className="text-display-2 font-semibold text-foreground mb-2">{t('roomPage.noPlayers')}</h3>
                <p className="text-body-lg text-muted-foreground">{t('roomPage.noPlayersDesc')}</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-2.5">
                {sortedPlayers.map((player, index) => {
                  const isTop3 = index < 3
                  const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success']
                  const color = colors[index % colors.length]
                  return (
                    <div
                      key={player._id}
                      className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 lg:p-4 flex items-center gap-2 sm:gap-3 lg:gap-4 transition-all hover:bg-muted/30 ${
                        isTop3 ? 'glass-card border border-primary/10' : 'glass-card'
                      }`}
                    >
                      <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 lg:w-8 text-center text-muted-foreground shrink-0">
                        {rankBadge(index + 1) || (index + 1)}
                      </span>
                      <div className={`max-[479px]:hidden w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${color} flex items-center justify-center text-[0.65rem] sm:text-small font-bold text-primary-foreground shrink-0`}>
                        {player.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handlePlayerGames(player)}
                          className="font-semibold text-small sm:text-nav truncate text-foreground hover:text-primary transition-colors text-left max-w-full"
                        >
                          {player.name}
                        </button>
                        <div className="flex items-center gap-2 sm:gap-3 mt-0.5">
                          <span className="text-small sm:text-nav font-semibold text-primary">{formatPoints(computeUserPoints(player), t)}</span>
                          <span className="text-small text-muted-foreground/60">·</span>
                          <span className="text-small text-muted-foreground">{player.predictionCount ?? player.predictions?.length ?? 0} {t('roomPage.pred')}</span>
                        </div>
                      </div>
                      <div className="flex gap-0.5 sm:gap-1 lg:gap-1.5 shrink-0">
                        {isOwner && room?.gameType === 'football' && (
                          <button
                            onClick={() => setStartingPointsPlayer(player)}
                            className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-amber-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                            aria-label={t('roomPage.setStartingPoints')}
                            title={t('roomPage.setStartingPoints')}
                          >
                            <Coins size={14} className="shrink-0" />
                          </button>
                        )}
                        <button
                          onClick={() => handlePlayerGames(player)}
                          className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-sky-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                          aria-label={t('roomPage.played')}
                        >
                          <Gamepad2 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.played')}</span>
                        </button>
                        <button
                          onClick={() => handlePlayerUpcoming(player)}
                          className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-red-500 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                          aria-label={t('roomPage.upcoming')}
                        >
                          <CalendarClock size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.upcoming')}</span>
                        </button>
                        <button
                          onClick={() => handlePlayerStats(player)}
                          className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-violet-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                          aria-label={t('roomPage.stats')}
                        >
                          <BarChart3 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.stats')}</span>
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Games & Predictions modals (nested: game list -> game detail) */}
      {roomId && (
        <>
          <GamesAndPredictionsModal
            roomId={roomId}
            isOpen={showGamesAndPredictionsModal}
            futureOnly
            onClose={() => setShowGamesAndPredictionsModal(false)}
            onSelectGame={(match) => {
              setSelectedGameForPredictions(match)
              setShowGamePredictionsModal(true)
            }}
          />
          <GamesAndPredictionsModal
            roomId={roomId}
            isOpen={showPlayedModal}
            playedOnly
            titleKey="roomPage.played"
            onClose={() => setShowPlayedModal(false)}
            onSelectGame={(match) => {
              setSelectedGameForPredictions(match)
              setShowPlayedModal(false)
              setShowGamePredictionsModal(true)
            }}
          />
          <GamePredictionsModal
            roomId={roomId}
            match={selectedGameForPredictions}
            isOpen={showGamePredictionsModal}
            onClose={() => {
              setShowGamePredictionsModal(false)
              setSelectedGameForPredictions(null)
            }}
          />
        </>
      )}

      {/* Modals */}
      {showRulesModal && roomId && (
        <RoomRulesModal
          isOpen={showRulesModal}
          roomId={roomId}
          onClose={() => setShowRulesModal(false)}
        />
      )}

      {showInviteModal && (
        <InvitePlayerModal
          roomId={roomId!}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            fetchRoomData(true) // Refresh room (and matches) so new player appears
          }}
        />
      )}

      {showEditModal && room && (
        <EditRoomModal
          room={room}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            fetchRoomData(true)
          }}
        />
      )}

      {showLeaveRoomModal && roomId && (
        <LeaveRoomModal
          roomId={roomId}
          roomName={room?.name}
          onClose={() => setShowLeaveRoomModal(false)}
        />
      )}

      {showDeleteRoomModal && roomId && (
        <DeleteRoomModal
          roomId={roomId}
          roomName={room?.name}
          onClose={() => setShowDeleteRoomModal(false)}
        />
      )}

      {showPlayWorldModal && publicFootballStatus && roomId && (
        <PlayWorldFromRoomModal
          roomId={roomId}
          status={publicFootballStatus}
          onClose={() => setShowPlayWorldModal(false)}
          onSuccess={() => refetchPublicFootballStatus()}
        />
      )}

      {showLeaveWorldModal && roomId && (
        <LeaveWorldFootballModal
          roomId={roomId}
          onClose={() => setShowLeaveWorldModal(false)}
          onSuccess={() => refetchPublicFootballStatus()}
        />
      )}

      {startingPointsPlayer && roomId && (
        <SetStartingPointsModal
          roomId={roomId}
          player={startingPointsPlayer}
          onClose={() => setStartingPointsPlayer(null)}
          onSuccess={() => {
            setStartingPointsPlayer(null)
            fetchRoomData(true)
          }}
        />
      )}

      {selectedPlayer && roomId && (
        <ViewPlayerStatsGamesModal
          player={selectedPlayer}
          isOpen={showPlayerModal}
          onClose={() => {
            if (playerDataRequestRef.current) {
              playerDataRequestRef.current.cancel('Modal closed')
              playerDataRequestRef.current = null
            }
            setShowPlayerModal(false)
            setSelectedPlayer(null)
            setIsLoadingPlayerData(false)
            setIsLoadingMorePredictions(false)
            setHasMorePlayerPredictions(false)
          }}
          initialViewType={modalViewType}
          roomId={roomId}
          gameType={room?.gameType === 'football' || room?.gameType === 'formula1' || room?.gameType === 'golf' ? room.gameType : (room?.setting != null ? 'football' : undefined)}
          isLoading={isLoadingPlayerData}
          hasMorePredictions={hasMorePlayerPredictions}
          isLoadingMore={isLoadingMorePredictions}
          onLoadMore={handleLoadMorePredictions}
        />
      )}

      {roomId && (
        <RoomMessengerModal
          roomId={roomId}
          isOpen={showMessengerModal}
          onClose={() => setShowMessengerModal(false)}
          onUnreadChange={setUnreadMessageCount}
        />
      )}

      {roomId && (
        <BestOfModal
          isOpen={showBestOfModal}
          roomId={roomId}
          onClose={() => {
            setShowBestOfModal(false)
            setIsLoadingBestOf(false)
          }}
          isLoading={isLoadingBestOf}
          bestPredictionsFromApi={bestPredictionsFromApi}
        />
      )}
    </div>
  )
}

export default Room
